import { Link } from "react-router-dom";
import { ShoppingCart, BookOpen, Headphones, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/components/CartContext";
import Footer from "@/components/Footer";

const BOOK_COVER = "/images/ebook-cover.jpg";
const AUDIOBOOK_ART = "/images/audiobook-cover.png";

const PRODUCTS = [
  {
    id: "digital",
    name: "eBook",
    subtitle: "Digital Download — EPUB Format",
    price: 14.99,
    image: BOOK_COVER,
    features: ["Instant digital delivery via email", "EPUB format — read on any device", "Lifetime access"],
    needsShipping: false,
  },
  {
    id: "early_bird",
    name: "Paperback",
    subtitle: "First Edition Physical Copy",
    price: 29.99,
    image: BOOK_COVER,
    features: ["First edition paperback", "Ships from Australia", "Available now"],
    needsShipping: true,
    badge: "Popular",
  },
  {
    id: "fyb_hat",
    name: "FYB Hat",
    subtitle: "Limited Edition — Light Pink on Black",
    price: 40.00,
    image: "/images/fyb-hat-pink-on-black.png",
    features: ["Embroidered FYB logo", "\"The 40 Year Bender\" on back", "Adjustable strap", "Limited edition run"],
    needsShipping: true,
    badge: "Limited",
  },
];

export default function StorePage() {
  const { addItem } = useCart();

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="store-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
          <span className="text-xs tracking-[0.15em] uppercase text-[#D4AF37]">Store</span>
        </div>
      </nav>

      {/* Hero */}
      <header className="pt-28 pb-12 px-6 text-center">
        <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">FYB Store</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4">
          Shop The Collection
        </h1>
        <p className="text-zinc-400 max-w-xl mx-auto">
          Books, merch, and the tools to find your breakthrough. Add items to your cart and check out in one go. Discount codes accepted at checkout.
        </p>
      </header>

      {/* Products */}
      <section className="pb-16 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRODUCTS.map((product) => (
            <div
              key={product.id}
              className={`relative border rounded-sm bg-[#121212] overflow-hidden transition-all duration-300 hover:translate-y-[-4px] ${
                product.badge ? "border-[#D4AF37]/50 hover:border-[#D4AF37]" : "border-white/10 hover:border-[#D4AF37]/50"
              }`}
              data-testid={`store-product-${product.id}`}
            >
              {product.badge && (
                <div className="absolute top-4 right-4 z-10 bg-[#D4AF37] text-[#050505] px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase rounded-sm">
                  {product.badge}
                </div>
              )}
              <div className="aspect-square overflow-hidden bg-[#0a0a0a] flex items-center justify-center p-6">
                <img src={product.image} alt={product.name} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <h3 className="font-heading text-2xl tracking-tight uppercase text-white">{product.name}</h3>
                  <p className="text-xs text-zinc-500 mt-1">{product.subtitle}</p>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-4xl text-[#D4AF37]">${product.price.toFixed(2)}</span>
                  <span className="text-xs text-zinc-500">AUD</span>
                </div>
                {product.needsShipping && <p className="text-[10px] text-zinc-600">+ shipping at checkout</p>}
                <Separator className="bg-white/10" />
                <ul className="space-y-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-zinc-300">
                      <ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => addItem(product.id, product.name, product.price)}
                  className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase flex items-center justify-center gap-2"
                  data-testid={`add-to-cart-${product.id}`}
                >
                  <ShoppingCart className="w-4 h-4" /> Add To Cart
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Audiobook */}
      <section className="py-16 px-6 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <img src={AUDIOBOOK_ART} alt="The Forty Year Bender Audiobook" className="w-48 mx-auto rounded-sm shadow-lg shadow-black/50" />
          <h2 className="font-heading text-2xl tracking-tight uppercase text-white">Audiobook</h2>
          <p className="text-zinc-400 text-sm">Narrated by Chris Merlo himself. Available on Awesound.</p>
          <div className="flex items-baseline justify-center gap-2">
            <span className="font-heading text-4xl text-[#D4AF37]">$14.99</span>
            <span className="text-xs text-zinc-500">AUD</span>
          </div>
          <a
            href="https://awesound.com/a/the-40-year-bender"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm"
          >
            <Headphones className="w-4 h-4" /> Listen on Awesound
          </a>
          <p className="text-zinc-600 text-[10px]">Audiobook is hosted on Awesound and processed separately.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
